// @ts-nocheck
import React, {
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Grid, TextField, Box } from '@mui/material';
import PropTypes from 'prop-types';

import { useDebouncedInput } from '../../../../hooks/useDebouncedInput';
import { getErrorHelperText } from '../../../../utils/Utils';
import { getCharacterLimitForAttribute } from '../../../../utils/formUtils';
import { INPUT_LABEL_PROPS } from './TextFieldComponent';

/**
 * Text field extended component for editing text attributes
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {string} props.value - Current field value
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Object} props.attribute - Attribute configuration object
 * @param {Object} props.entityDetailsFormikForm - Formik form instance
 * @param {string} props.titleLabel - Title label for the field
 * @returns {React.ReactElement} The TextFieldExtended component
 */
const TextFieldExtendedComponent = forwardRef(
  function TextFieldExtendedComponent(
    {
      value,
      onChange,
      attribute,
      entityDetailsFormikForm,
      label,
      titleLabel,
      isLoadingConstraints = false,
    },
    ref
  ) {
    const MAX_LENGTH = useMemo(
      () => getCharacterLimitForAttribute(attribute) ?? 10000,
      [attribute]
    );

    const syncedValue = useMemo(() => {
      const attrId = attribute?.id;
      const formikVal =
        entityDetailsFormikForm?.values?.[attrId] ??
        entityDetailsFormikForm?.values?.[Number(attrId)];
      const resolved = formikVal ?? value;

      return resolved === undefined || resolved === null
        ? ''
        : String(resolved);
    }, [value, attribute?.id, entityDetailsFormikForm?.values]);

    const { localValue, setValue, flush } = useDebouncedInput(
      syncedValue,
      onChange
    );

    useImperativeHandle(ref, () => ({ flush }), [flush]);

    const characterCount = useMemo(() => localValue.length, [localValue]);

    const { onBlur: formikBlur, ...formikFieldProps } =
      entityDetailsFormikForm?.getFieldProps(attribute?.id) || {};

    /**
     * Handles input change events
     * @function
     * @param {React.ChangeEvent<HTMLInputElement>} e - The change event
     * @returns {void}
     */
    const handleChange = useCallback(
      (e) => {
        const newValue = e.target.value;
        if (newValue.length <= MAX_LENGTH) {
          setValue(newValue);
        }
      },
      [setValue]
    );

    const handleBlur = useCallback(
      (event) => {
        flush();
        formikBlur?.(event);
      },
      [flush, formikBlur]
    );

    const errorHelperText = useMemo(
      () =>
        getErrorHelperText(
          entityDetailsFormikForm?.errors?.[attribute?.id],
          entityDetailsFormikForm?.touched?.[attribute?.id]
        ),
      [
        entityDetailsFormikForm?.errors,
        entityDetailsFormikForm?.touched,
        attribute?.id,
      ]
    );

    // True =>reached the character limit
    const atLimit = useMemo(
      () => characterCount >= MAX_LENGTH,
      [characterCount, MAX_LENGTH]
    );

    const helperTextContent = useMemo(() => {
      const counterColor = atLimit ? 'error.main' : 'text.secondary';

      const counter = (
        <Box component="span" sx={{ marginLeft: 'auto', color: counterColor }}>
          {characterCount}/{MAX_LENGTH}
        </Box>
      );

      if (atLimit) {
        return (
          <Box
            component="span"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <span>{`Only ${MAX_LENGTH} characters are allowed`}</span>
            {counter}
          </Box>
        );
      }

      if (errorHelperText) {
        return (
          <Box
            component="span"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <span>{errorHelperText}</span>
            {counter}
          </Box>
        );
      }
      return (
        <Box
          component="span"
          sx={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          <Box component="span" sx={{ color: counterColor }}>
            {characterCount}/{MAX_LENGTH}
          </Box>
        </Box>
      );
    }, [errorHelperText, characterCount, atLimit, MAX_LENGTH]);

    return (
      <Grid
        container
        size={12}
        sx={{
          gap: 2,
        }}
      >
        <Grid size={12}>
          <TextField
            {...formikFieldProps}
            title={titleLabel}
            label={label}
            fullWidth
            value={localValue}
            placeholder="Enter a description"
            onChange={handleChange}
            onBlur={handleBlur}
            multiline
            minRows={8}
            spellCheck
            error={Boolean(errorHelperText) || atLimit}
            helperText={helperTextContent}
            disabled={!attribute?.is_editable}
            slotProps={{
              input: {
                readOnly: isLoadingConstraints,
                maxLength: MAX_LENGTH,
              },

              htmlInput: {
                maxLength: MAX_LENGTH,
              },

              inputLabel: INPUT_LABEL_PROPS,
            }}
          />
        </Grid>
      </Grid>
    );
  }
);

TextFieldExtendedComponent.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    is_editable: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    getFieldProps: PropTypes.func.isRequired,
    errors: PropTypes.objectOf(PropTypes.string).isRequired,
    touched: PropTypes.objectOf(PropTypes.bool).isRequired,
    values: PropTypes.objectOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])
    ),
  }).isRequired,
  label: PropTypes.string.isRequired,
  titleLabel: PropTypes.string.isRequired,
  isLoadingConstraints: PropTypes.bool,
};

export default TextFieldExtendedComponent;
